import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FaArrowLeft,
  FaTimes,
  FaInfo,
  FaFile,
  FaDownload,
  FaThumbsUp,
  FaThumbsDown,
  FaEnvelope,
  FaPrint,
  FaCloud,
  FaFolder,
  FaFileDownload,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import api from '~/services/api';
import { Spinner } from '~/components/Spinner';
import MobileReceiverModal from '../List/MobileReceiverModal';

import {
  Container,
  Header,
  BackButton,
  ProtocolDetail,
  StatusBadge,
  AttachmentsList,
  AttachmentItem,
} from './styles';

const MobileProtocolDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const state = location.state || {};

  const searchParams = new URLSearchParams(location.search);
  const openWriteOffByParam = searchParams.get('writeoff') === 'true';

  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);

  const [receiverModalIsOpen, setReceiverModalIsOpen] = useState(
    !!state.openWriteOff || openWriteOffByParam
  );

  const [writeOffDate] = useState(state.writeOffDate || null);

  const loadProtocolDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`protocols/${id}`);
      setProtocol(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do protocolo');
      history.push('/mobile-protocol');
    } finally {
      setLoading(false);
    }
  }, [id, history]);

  useEffect(() => {
    if (id) {
      loadProtocolDetails();
    }
  }, [loadProtocolDetails, id]);

  // Função para download de arquivo
  const downloadFile = useCallback(async (blobName, fileName) => {
    try {
      const response = await api.get(`files/${blobName}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Erro ao baixar o arquivo');
    }
  }, []);

  // Função para abrir arquivo
  const openFile = useCallback(async blobName => {
    try {
      const response = await api.get(`files/${blobName}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Erro ao abrir o arquivo');
    }
  }, []);

  // Função para dar baixa no protocolo, agora recebe também a foto em base64
  const handleWriteOff = useCallback(
    async (receiverName, receiverDate, photoBase64) => {
      if (protocol && receiverName.trim() !== '') {
        try {
          await api.post('/protocols/document_views', {
            protocol_id: protocol.id,
            type: 1,
            name: receiverName,
            hasDownloaded: true,
            updated_at: receiverDate,
            photo: photoBase64, // campo novo para imagem
          });

          setReceiverModalIsOpen(false);

          // Atualizar o protocolo para refletir a mudança
          const updatedProtocol = { ...protocol, situation: 2 };
          setProtocol(updatedProtocol);

          toast.success('Baixa realizada com sucesso!', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } catch {
          toast.error('Falha ao dar baixa no protocolo', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          setReceiverModalIsOpen(false);
        }
      }
    },
    [protocol]
  );

  const handleBackToList = useCallback(() => {
    history.push('/mobile-protocol');
  }, [history]);

  if (loading) {
    return (
      <Container>
        <div className="loading-container">
          <Spinner size={30} />
          <p>Carregando protocolo...</p>
        </div>
      </Container>
    );
  }

  if (!protocol) {
    return (
      <Container>
        <Header>
          <BackButton type="button" onClick={handleBackToList}>
            <FaArrowLeft /> Voltar
          </BackButton>
          <h2>Protocolo não encontrado</h2>
        </Header>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Header>
          <BackButton type="button" onClick={handleBackToList}>
            <FaArrowLeft /> Voltar
          </BackButton>
          <h2>Detalhes do Protocolo</h2>
        </Header>

        <ProtocolDetail>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 24,
            }}
          >
            <h3 style={{ margin: 0 }}>Protocolo #{protocol.cod}</h3>
            <StatusBadge
              className={
                protocol.situation === 2
                  ? 'concluido'
                  : protocol.situation === 5
                  ? 'cancelado'
                  : 'pendente'
              }
            >
              {protocol.situation !== 2 && protocol.situation !== 5 && (
                <>
                  <FaThumbsDown size={18} color="#E53935" />
                  Pendente
                </>
              )}
              {protocol.situation === 2 && (
                <>
                  <FaThumbsUp size={18} color="#006229" />
                  Entregue
                </>
              )}
              {protocol.situation === 5 && (
                <>
                  <FaTimes size={18} color="#E53935" />
                  Cancelado
                </>
              )}
            </StatusBadge>
          </div>

          <div className="info-group">
            <p>
              <strong>Cliente:</strong> {protocol.client?.name || '-'}
            </p>
            <p>
              <strong>Data:</strong>{' '}
              {protocol.created_at
                ? format(new Date(protocol.created_at), 'dd/MM/yyyy', {
                    locale: ptBR,
                  })
                : '-'}
            </p>
            <p>
              <strong>Método:</strong>{' '}
              {protocol.method === 0 && (
                <span className="method-detail">
                  <FaEnvelope size={16} color="#01579B" /> Email
                </span>
              )}
              {protocol.method === 1 && (
                <span className="method-detail">
                  <FaPrint size={16} color="#006229" /> Impresso
                </span>
              )}
              {protocol.method === 2 && (
                <span className="method-detail">
                  <FaCloud size={16} color="#44546a" /> Digital
                </span>
              )}
              {protocol.method === 3 && (
                <span className="method-detail">
                  <FaFolder size={16} color="#006229" /> Pasta
                </span>
              )}
              {!protocol.method && '-'}
            </p>
            <p>
              <strong>Responsável:</strong> {protocol.user?.name || '-'}
            </p>
          </div>

          {protocol.attachments && protocol.attachments.length > 0 && (
            <>
              <h4>Anexos</h4>
              <AttachmentsList>
                {protocol.attachments.map(attachment => (
                  <AttachmentItem key={attachment.id}>
                    <div className="attachment-info">
                      <FaFile />
                      <span>{attachment.name}</span>
                    </div>
                    <div className="attachment-actions">
                      <button
                        type="button"
                        onClick={() => openFile(attachment.blobName)}
                        title="Visualizar"
                      >
                        <FaInfo />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          downloadFile(attachment.blobName, attachment.name)
                        }
                        title="Baixar"
                      >
                        <FaDownload />
                      </button>
                    </div>
                  </AttachmentItem>
                ))}
              </AttachmentsList>
            </>
          )}

          {/* Botões de ação */}
          <div className="action-buttons">
            {protocol.situation !== 2 && protocol.situation !== 5 && (
              <button
                type="button"
                className="write-off-button"
                onClick={() => setReceiverModalIsOpen(true)}
              >
                <FaFileDownload /> Dar Baixa
              </button>
            )}
          </div>
        </ProtocolDetail>
      </Container>

      <MobileReceiverModal
        isOpen={receiverModalIsOpen}
        setIsOpen={setReceiverModalIsOpen}
        handleWriteOff={handleWriteOff}
        protocolCod={protocol?.cod}
        client={protocol?.client?.name || 'Cliente não informado'}
        method={protocol?.method}
        forcedDate={writeOffDate}
      />
    </>
  );
};

export default MobileProtocolDetail;
